import React from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { createLogger } from 'libs/end-to-end/src/isomorphic-logger';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { useSearchParams } from 'react-router-dom';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';

const logger = createLogger({ name: 'useLoadedMetadata' });

// This hook is used to get the share link search parameter from the URL.
// It can be used as a way to determine if a share link is present in the URL.
// Use cases may include needing flags to switch off autoloaders when a share link is present.
export const useShareLinkSearchParameter = (): string | null => {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    return searchParams.get('data');
  }, [searchParams]);
};

export interface UseShareLinkHook<T> {
  shareLinkRequest: T | null;
  shareLinkLoadAttempted: boolean;
}
export const useShareLinkRequest = <T,>({
  myUser,
  userAuthenticatedApiCall,
  type,
  onLoaded,
  canLoad,
}: {
  myUser: MyUser | null;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  type: S2SafeParsable<unknown, T>;
  onLoaded?: (request: T | null) => void;
  canLoad?: boolean;
}): UseShareLinkHook<T> => {
  const data = useShareLinkSearchParameter();

  const [shareLinkLoadAttempted, setShareLinkLoadAttempted] = useState(false);

  const [shareLinkRequest, setShareLinkRequest] = useState<T | null>(null);

  const lastLoadedData = useRef<string | null>(null);
  const canLoadResolve = canLoad === undefined ? true : canLoad;
  const isEmbedMode = useEmbedMode();

  useAsyncEffect(
    async (isAlive) => {
      if (!isAlive() || !myUser || !canLoadResolve || isEmbedMode) {
        return;
      }

      if (data === lastLoadedData.current) {
        return;
      }

      lastLoadedData.current = data;

      setShareLinkLoadAttempted(false);

      try {
        if (data === null) {
          return;
        }

        logger.info('starting execution');
        const dataParseResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/share/metadata?data=${encodeURIComponent(data)}`,
          requestOptions: {
            method: 'GET',
          },
          responseSchema: type,
        });
        logger.info(dataParseResult);

        if (!dataParseResult || dataParseResult.resultType === 'err') {
          return;
        }

        const result = dataParseResult.value;
        setShareLinkRequest(result);
        if (onLoaded) {
          onLoaded(result);
        }
      } finally {
        if (isAlive()) {
          setShareLinkLoadAttempted(true);
        }
      }
    },
    [
      canLoadResolve,
      data,
      isEmbedMode,
      myUser,
      onLoaded,
      type,
      userAuthenticatedApiCall,
    ],
  );

  return useMemo(() => {
    return {
      shareLinkRequest,
      shareLinkLoadAttempted,
    };
  }, [shareLinkLoadAttempted, shareLinkRequest]);
};
